<p-drawer [(visible)]="visible" position="right" [style]="{width: '600px'}" [modal]="true"
          header="Versionsverlauf" (onHide)="closeDialog()">

    <div class="flex flex-column gap-3">
        <!-- Header Actions -->
        <div class="flex justify-content-between align-items-center mb-2">
            <span class="text-lg font-semibold">
                <i class="fa-solid fa-code-branch mr-2"></i>
                Selektionskriterien-Versionen
            </span>
            <p-button *ngIf="versionHistory?.versions?.length >= 2"
                      label="Vergleichen"
                      icon="fa-solid fa-code-compare"
                      [outlined]="true"
                      size="small"
                      (onClick)="openCompareDialog()">
            </p-button>
        </div>

        <!-- Loading State -->
        <div *ngIf="loading" class="flex justify-content-center p-4">
            <p-progressSpinner [style]="{width: '50px', height: '50px'}"></p-progressSpinner>
        </div>

        <!-- Empty State -->
        <div *ngIf="!loading && (!versionHistory?.versions || versionHistory.versions.length === 0)"
             class="flex flex-column align-items-center p-4 text-color-secondary">
            <i class="fa-solid fa-history text-4xl mb-3"></i>
            <span> Kein Versionsverlauf verfügbar</span>
        </div>

        <!-- Version List -->
        <div *ngIf="!loading && versionHistory?.versions?.length > 0" class="flex flex-column gap-2">
            <div *ngFor="let version of versionHistory.versions"
                 class="surface-card border-1 surface-border border-round p-3 hover:surface-hover cursor-pointer"
                 [class.border-primary]="version.isActive">

                <div class="flex justify-content-between align-items-start mb-2">
                    <div class="flex align-items-center gap-2">
                        <span class="font-semibold">Version {{ version.versionNumber }}</span>
                        <p-tag *ngIf="version.isActive" value="Aktiv" severity="success" [rounded]="true"></p-tag>
                    </div>
                    <div class="flex gap-1">
                        <p-button icon="fa-solid fa-eye"
                                  [rounded]="true"
                                  [text]="true"
                                  size="small"
                                  pTooltip="Details anzeigen"
                                  (onClick)="viewVersionDetail(version)">
                        </p-button>
                        <p-button *ngIf="version.canRollback"
                                  icon="fa-solid fa-history"
                                  [rounded]="true"
                                  [text]="true"
                                  severity="warn"
                                  size="small"
                                  pTooltip="Auf diese Version zurücksetzen"
                                  (onClick)="confirmRollback(version)">
                        </p-button>
                        <p-button *ngIf="!version.isActive"
                                  icon="fa-solid fa-check-circle"
                                  [rounded]="true"
                                  [text]="true"
                                  severity="success"
                                  size="small"
                                  pTooltip="Diese Version aktivieren"
                                  (onClick)="activateVersion(version)">
                        </p-button>
                    </div>
                </div>

                <div *ngIf="version.changeSummary" class="text-color-secondary text-sm mb-2">
                    {{ version.changeSummary }}
                </div>

                <div class="flex gap-4 text-sm text-color-secondary">
                    <span>
                        <i class="fa-solid fa-calendar mr-1"></i>
                        {{ formatDate(version.createdAt) }}
                    </span>
                    <span *ngIf="version.createdByUserName">
                        <i class="fa-solid fa-user mr-1"></i>
                        {{ version.createdByUserName }}
                    </span>
                </div>

                <div *ngIf="version.groupCount !== undefined || version.conditionCount !== undefined"
                     class="flex gap-3 mt-2 text-sm">
                    <span *ngIf="version.groupCount !== undefined">
                        <i class="fa-solid fa-layer-group mr-1"></i>
                        {{ version.groupCount }} Gruppen
                    </span>
                    <span *ngIf="version.conditionCount !== undefined">
                        <i class="fa-solid fa-filter mr-1"></i>
                        {{ version.conditionCount }} Bedingungen
                    </span>
                </div>
            </div>
        </div>
    </div>
</p-drawer>

<!-- Version Detail Dialog -->
<p-dialog [(visible)]="showDetailDialog"
          [header]="'Version ' + (selectedVersion?.versionNumber || '')"
          [modal]="true"
          [style]="{width: '800px', maxHeight: '80vh'}"
          [draggable]="false">

    <div *ngIf="loadingDetail" class="flex justify-content-center p-4">
        <p-progressSpinner [style]="{width: '50px', height: '50px'}"></p-progressSpinner>
    </div>

    <div *ngIf="!loadingDetail && selectedVersion" class="flex flex-column gap-4">
        <!-- Version Info -->
        <div class="surface-ground border-round p-3">
            <div class="grid">
                <div class="col-6">
                    <div class="text-color-secondary text-sm">Erstellt</div>
                    <div>{{ formatDate(selectedVersion.createdAt) }}</div>
                    <div *ngIf="selectedVersion.createdByUserName" class="text-sm">
                        von {{ selectedVersion.createdByUserName }}
                    </div>
                </div>
                <div class="col-6">
                    <div class="text-color-secondary text-sm">Aktiviert</div>
                    <div>{{ formatDate(selectedVersion.activatedAt) }}</div>
                    <div *ngIf="selectedVersion.activatedByUserName" class="text-sm">
                        von {{ selectedVersion.activatedByUserName }}
                    </div>
                </div>
            </div>
            <div *ngIf="selectedVersion.changeSummary" class="mt-3">
                <div class="text-color-secondary text-sm">Zusammenfassung der Änderungen</div>
                <div>{{ selectedVersion.changeSummary }}</div>
            </div>
        </div>

        <!-- Usage History -->
        <div *ngIf="selectedVersion.usages?.length > 0">
            <div class="font-semibold mb-2">
                <i class="fa-solid fa-chart-line mr-2"></i>
                Selektionsläufe mit dieser Version
            </div>
            <p-table [value]="selectedVersion.usages" [scrollable]="true" scrollHeight="200px">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Lauftyp</th>
                        <th>Ausgeführt</th>
                        <th>Hinzugefügt</th>
                        <th>Entfernt</th>
                        <th>Status</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-usage>
                    <tr>
                        <td>{{ usage.runType }}</td>
                        <td>{{ formatDate(usage.executedAt) }}</td>
                        <td class="text-green-500">+{{ usage.membersAddedCount || 0 }}</td>
                        <td class="text-red-500">-{{ usage.membersRemovedCount || 0 }}</td>
                        <td>
                            <p-tag [value]="usage.runStatus"
                                   [severity]="usage.runStatus === 'SUCCESS' ? 'success' : 'danger'">
                            </p-tag>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Criteria Preview -->
        <div *ngIf="selectedVersion.criteriaSnapshot">
            <div class="font-semibold mb-2">
                <i class="fa-solid fa-tree mr-2"></i>
                Kriterienschnappschuss
            </div>
            <div class="surface-ground border-round p-3 overflow-auto" style="max-height: 300px">
                <pre class="text-sm m-0">{{ selectedVersion.criteriaSnapshot | json }}</pre>
            </div>
        </div>
    </div>
</p-dialog>

<!-- Compare Versions Dialog -->
<p-dialog [(visible)]="showCompareDialog"
          header="Versionen vergleichen"
          [modal]="true"
          [style]="{width: '900px', maxHeight: '80vh'}"
          [draggable]="false">

    <div class="flex flex-column gap-4">
        <!-- Version Selection -->
        <div class="flex gap-4 align-items-center">
            <div class="flex-1">
                <label class="block text-sm font-medium mb-1">Von Version</label>
                <p-select [(ngModel)]="compareFromVersion"
                          [options]="getVersionOptions()"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Version auswählen"
                          [style]="{width: '100%'}">
                </p-select>
            </div>
            <i class="fa-solid fa-arrow-right text-color-secondary mt-3"></i>
            <div class="flex-1">
                <label class="block text-sm font-medium mb-1">Zu Version</label>
                <p-select [(ngModel)]="compareToVersion"
                          [options]="getVersionOptions()"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Version auswählen"
                          [style]="{width: '100%'}">
                </p-select>
            </div>
            <p-button label="Vergleichen"
                      icon="fa-solid fa-code-compare"
                      class="mt-3"
                      [loading]="loadingComparison"
                      (onClick)="compareVersions()">
            </p-button>
        </div>

        <!-- Comparison Results -->
        <div *ngIf="loadingComparison" class="flex justify-content-center p-4">
            <p-progressSpinner [style]="{width: '50px', height: '50px'}"></p-progressSpinner>
        </div>

        <div *ngIf="!loadingComparison && versionComparison">
            <div *ngIf="versionComparison.changes?.length === 0"
                 class="text-center text-color-secondary p-4">
                <i class="fa-solid fa-equals text-3xl mb-2"></i>
                <div>Keine Unterschiede zwischen den Versionen gefunden</div>
            </div>

            <div *ngIf="versionComparison.changes?.length > 0" class="flex flex-column gap-2">
                <div class="font-semibold mb-2">
                    {{ versionComparison.changes.length }} Änderung(en) erkannt
                </div>

                <div *ngFor="let change of versionComparison.changes"
                     class="surface-card border-1 surface-border border-round p-3">
                    <div class="flex align-items-center gap-2 mb-2">
                        <i [class]="getChangeTypeIcon(change.changeType)"></i>
                        <i [class]="getElementTypeIcon(change.elementType)" class="text-color-secondary"></i>
                        <span class="font-medium">{{ change.description }}</span>
                    </div>

                    <div *ngIf="change.elementPath" class="text-sm text-color-secondary mb-2">
                        <i class="fa-solid fa-location-dot mr-1"></i>
                        {{ change.elementPath }}
                    </div>

                    <div class="grid" *ngIf="change.previousValue || change.newValue">
                        <div class="col-6" *ngIf="change.previousValue">
                            <div class="text-sm text-color-secondary">Vorher</div>
                            <div class="text-red-500">{{ change.previousValue }}</div>
                        </div>
                        <div class="col-6" *ngIf="change.newValue">
                            <div class="text-sm text-color-secondary">Neu</div>
                            <div class="text-green-500">{{ change.newValue }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</p-dialog>

<!-- Rollback Confirmation Dialog -->
<p-dialog [(visible)]="showRollbackDialog"
          header="Auf vorherige Version zurücksetzen"
          [modal]="true"
          [style]="{width: '500px'}"
          [draggable]="false">

    <div *ngIf="rollbackTargetVersion" class="flex flex-column gap-4">
        <div class="surface-ground border-round p-3">
            <div class="flex align-items-center gap-2 mb-2">
                <i class="fa-solid fa-history text-warning text-xl"></i>
                <span class="font-semibold">Version {{ rollbackTargetVersion.versionNumber }}</span>
            </div>
            <div *ngIf="rollbackTargetVersion.changeSummary" class="text-color-secondary">
                {{ rollbackTargetVersion.changeSummary }}
            </div>
            <div class="text-sm text-color-secondary mt-2">
                Erstellt: {{ formatDate(rollbackTargetVersion.createdAt) }}
            </div>
        </div>

        <div>
            <label class="block text-sm font-medium mb-1">Grund für Zurücksetzung (optional)</label>
            <textarea pInputTextarea
                      [(ngModel)]="rollbackReason"
                      [rows]="3"
                      placeholder="Grund für die Zurücksetzung eingeben..."
                      class="w-full">
            </textarea>
        </div>

        <div class="surface-warning border-round p-3 text-warning">
            <i class="fa-solid fa-triangle-exclamation mr-2"></i>
            Es wird eine neue Version mit den Kriterien aus Version {{ rollbackTargetVersion.versionNumber }} erstellt.
            Die aktuellen Kriterien werden ersetzt.
        </div>
    </div>

    <ng-template pTemplate="footer">
        <p-button label="Abbrechen"
                  [text]="true"
                  (onClick)="showRollbackDialog = false">
        </p-button>
        <p-button label="Zurücksetzen"
                  icon="fa-solid fa-history"
                  severity="warn"
                  (onClick)="executeRollback()">
        </p-button>
    </ng-template>
</p-dialog>

<p-confirmDialog></p-confirmDialog>
